import Link from '@/components/link-component'
import { cx } from '@/utils/strings'
import { isTypeComponentIcon, type TypeComponentIcon } from '@/types/ctf'

import ArrowRight from '@/icons/bwi/angle-right.svg'

export const buttonStyles = {
  btnArrow: 'Arrow Link',
  btnOutlined: 'Button: Outlined',
  btnSolid: 'Button: Solid',
  btnGhost: 'Button: Ghost',
  btnPlain: 'Plain Link',
  btnText: 'Text Link',
}

const { btnSolid, btnOutlined, btnGhost, btnArrow, btnPlain, btnText } = buttonStyles

export type DisplayType = 'Inline' | 'Block'
export type AlignType = 'Left' | 'Center'
export type SizeType = 'Smaller' | 'Base' | 'Larger'
export type VariantType = typeof btnArrow | typeof btnOutlined | typeof btnSolid | typeof btnGhost | typeof btnPlain | typeof btnText

export type CtaLinkProps = {
  align?: AlignType // Used in component logic
  asButton?: boolean // Used to determine rendering
  hasDarkBg?: boolean // Used in styling logic
  bold?: boolean // Used in styling logic
  children?: any // Used to render content
  className?: string // Used for styling
  css?: string // Used in buttonStyle
  disabled?: boolean // Disabled button style
  display?: DisplayType // Used in styling
  icon?: TypeComponentIcon<'WITHOUT_UNRESOLVABLE_LINKS'> | string // Used for icon rendering
  iconPosition?: string // Used for icon positioning
  internalName?: string // Default provided but not directly used in rendering
  onClick?: React.MouseEventHandler<HTMLAnchorElement> // Would be spread in ...other
  plausibleEventName?: string // Used for analytics class
  scrollToOffset?: number // Would be spread in ...other
  size?: SizeType // Used for styling
  style?: VariantType // Used for styling variations
  to?: string // Used for link destination
  [key: string]: any // Catch-all for other props
}

export default function CtaLink({
  align = 'Left',
  asButton = false,
  hasDarkBg,
  bold = false,
  children,
  css,
  disabled,
  display = 'Inline',
  icon = undefined,
  iconPosition = 'Left',
  className: _className,
  plausibleEventName,
  size = 'Base',
  style = btnArrow,
  to,
  overideDarkMode,
  ...other
}: CtaLinkProps): JSX.Element {
  // remove props we don't want to pass to the button or link
  delete other?.link
  delete other?.internalName

  /**
   * Focus styles for the button
   *
   * Note: Specify the bg & border color with `focus:before:` where you apply this
   */
  const btnFocusStyle = [
    'before:content-[""]',
    'before:absolute',
    'before:top-0',
    'before:left-0',
    'before:w-full',
    'before:h-full',
    'before:rounded-full',
    'before:transition-colors',
    'focus:before:z-10',
    'focus:outline-hidden',
    'focus:before:border-2',
    'focus:before:border-dashed',
    'not-disabled:before:animate-button-shrink',
    'not-disabled:hover:before:animate-button-grow',
  ].join(' ')

  const textFocusStyle = [
    'focus:outline-hidden focus:border-b focus:border-secondary-700 dark:focus:border-primaryBlue',
    '[&_.arrow]:transform-gpu hover:[&_.arrow]:translate-x-1 group-hover:[&_.arrow]:translate-x-1',
  ].join(' ')

  let className = _className || ''
  if (plausibleEventName?.trim()) {
    className += ` ${plausibleEventName}`
  }

  const buttonStyle = cx(
    'font-medium z-0',
    size === 'Smaller' && 'text-sm',
    size === 'Base' && 'text-base',
    size === 'Larger' && 'text-lg',
    align === 'Center' && 'mx-auto',
    display === 'Inline' && 'inline-block',
    display === 'Block' && 'table',
    !disabled &&
      (btnArrow === style || btnPlain === style) &&
      !hasDarkBg &&
      `border-b-1 border-b-transparent text-primaryBlue hover:text-primary-700 dark:hover:text-primaryBlue ${textFocusStyle}`,
    !disabled &&
      (btnArrow === style || btnPlain === style) &&
      hasDarkBg &&
      `border-b-1 border-b-transparent text-white hover:text-primary-100 dark:hover:text-primaryBlue ${textFocusStyle}`,
    (style === btnOutlined || style === btnSolid || style === btnGhost) && 'relative rounded-full text-center',
    size === 'Smaller' && (style === btnOutlined || style === btnSolid || style === btnGhost) && 'px-3 py-1.5',
    size === 'Base' && (style === btnOutlined || style === btnSolid || style === btnGhost) && 'px-5 py-3',
    size === 'Larger' && (style === btnOutlined || style === btnSolid || style === btnGhost) && 'px-9 py-4',
    btnPlain === style && 'underline text-primaryBlue ',
    !disabled &&
      btnSolid === style &&
      !hasDarkBg &&
      `bg-primaryBlue text-white dark:text-secondary-800 cta-link-hover-no-bg ${btnFocusStyle} focus:before:border-white dark:focus:before:border-secondary-800`,
    btnOutlined === style && `${btnFocusStyle} before:border-2`,
    !disabled &&
      btnOutlined === style &&
      !hasDarkBg &&
      `text-primaryBlue cta-link-hover-no-bg ${btnFocusStyle} before:border-primaryBlue hover:before:border-white dark:hover:before:border-secondary-800 focus:before:border-white dark:focus:before:border-secondary-800`,
    !disabled &&
      style === btnOutlined &&
      hasDarkBg &&
      `text-white before:border-white hover:text-primary-500 hover:focus:before:border-primary-500 cta-link-hover after:bg-white`,
    // Ghost button styling - transparent initially, solid on hover
    !disabled &&
      btnGhost === style &&
      !hasDarkBg &&
      `text-primary-700 bg-transparent hover:bg-primaryBlue hover:text-white transition-color duration-200 dark:text-secondary-200 dark:hover:bg-white dark:hover:text-primary-700`,
    !disabled &&
      style === btnGhost &&
      hasDarkBg &&
      `text-white bg-transparent hover:bg-white hover:text-primary-500 transition-color duration-200`,
    !disabled &&
      style === btnSolid &&
      hasDarkBg &&
      `bg-white text-primary-500 before:border-white hover:text-primary-500 ${btnFocusStyle} focus:before:border-primary-500 cta-link-hover after:bg-primary-100`,
    btnText === style && 'no-underline',
    css,
    className.trim(),
    disabled && 'cursor-disabled focus:outline-hidden',
    disabled && (btnArrow === style || btnPlain === style) && !hasDarkBg && 'text-muted',
    disabled && (btnArrow === style || btnPlain === style) && hasDarkBg && 'text-[#A8B3C8]',
    disabled && !hasDarkBg && btnSolid === style && 'bg-secondary-300 dark:bg-secondary-600 text-muted',
    disabled &&
      !hasDarkBg &&
      btnOutlined === style &&
      'before:border-secondary-300 hover:before:border-secondary-300 text-muted',
    disabled && hasDarkBg && btnSolid === style && 'bg-[#A8B3C8] hover:bg-[#A8B3C8] text-muted-base',
    disabled && hasDarkBg && btnOutlined === style && 'before:border-[#A8B3C8] text-muted-base hover:bg-transparent',
    overideDarkMode &&
      !disabled &&
      btnSolid === style &&
      !hasDarkBg &&
      `bg-primary-500! text-white! after:bg-midnight-gradient!`,
    overideDarkMode &&
      !disabled &&
      btnOutlined === style &&
      !hasDarkBg &&
      `text-primary-500! after:bg-midnight-gradient! hover:text-white! before:border-primary-500!`,
    overideDarkMode && !disabled && (btnArrow === style || btnPlain === style) && !hasDarkBg && `text-primary-500!`,
    disabled && hasDarkBg && btnSolid === style && 'bg-[#A8B3C8] hover:bg-[#A8B3C8] text-muted-base',
    disabled && hasDarkBg && btnOutlined === style && 'before:border-[#A8B3C8] text-muted-base hover:bg-transparent',
    disabled && !hasDarkBg && btnGhost === style && 'bg-transparent text-muted hover:bg-transparent hover:text-muted',
    disabled && hasDarkBg && btnGhost === style && 'bg-transparent text-muted-base hover:bg-transparent hover:text-muted-base'
  )

  const renderBtnText = () => {
    const unicode = icon?.fields?.unicode || icon
    return (
      <span className={cx('flex items-center', bold && 'font-bold')}>
        {unicode && iconPosition === 'Left' && (
          <span className="font-bwi mr-2 text-base">{String.fromCodePoint(parseInt(unicode, 16))}</span>
        )}
        {children}
        {btnArrow === style && (
          <ArrowRight className="arrow ml-[0.375rem] w-[0.875rem] h-[0.875rem] transition-transform duration-250 delay-75" />
        )}
        {unicode && iconPosition === 'Right' && (
          <span className="font-bwi ml-2 text-base">{String.fromCodePoint(parseInt(unicode, 16))}</span>
        )}
      </span>
    )
  }

  return asButton ? (
    <button
      disabled={disabled}
      type="button"
      aria-label={to && `go to ${to}`}
      data-testid="cta-link"
      className={buttonStyle}
      {...other}
    >
      {renderBtnText()}
    </button>
  ) : (
    <Link aria-label={`go to ${to}`} to={to} className={buttonStyle} data-testid="cta-link" {...other}>
      {renderBtnText()}
    </Link>
  )
}
